// ChatWindow.js
import React, { useState, useRef, useEffect } from 'react';
import {
  Paper,
  Box,
  Typography,
  Avatar,
  IconButton,
  TextField,
  InputAdornment
} from '@mui/material';
import { CloseRounded } from '@mui/icons-material';
import { useMessenger } from './MessengerContext';
import { stringToColor, getInitials, formatTime } from './MessengerUtils';

const ChatWindow = ({ chat, position, preloadedMessages }) => {
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef(null);
  const { closeChat, sendMessage, currentUser } = useMessenger();
  
  // If we have preloaded messages and chat doesn't have them yet, use them
  useEffect(() => {
    if (preloadedMessages && preloadedMessages.length > 0 && 
        (!chat.messages || chat.messages.length === 0)) {
      console.log(`Using ${preloadedMessages.length} preloaded messages for chat ${chat.id || chat.chat_uuid}`);
      // This will be handled by the messenger context
    }
  }, [chat, preloadedMessages]);
  
  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chat.messages]);
  
  if (!chat || !chat.participants || !chat.participants[0]) return null;
  
  const contact = chat.participants[0];
  const rightPosition = position * 320 + 20;
  
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    
    // Send message to backend
    sendMessage(chat.id || chat.chat_uuid, newMessage);
    
    // Clear input
    setNewMessage('');
  };
  
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };
  
  // Determine if a message was sent by the current user (therapist)
  const isMessageFromMe = (message) => {
    // Get real therapist UUID from localStorage
    const realTherapistUuid = localStorage.getItem('userUuid');
    
    // First check against real UUID from localStorage (this is what your messages use)
    if (message.therapist_uuid && message.therapist_uuid === realTherapistUuid) {
      return true;
    }
    
    // Then check the other formats
    return (
      message.senderId === 'current-user' || 
      (message.therapist_uuid && message.therapist_uuid === currentUser?.uuid) ||
      (!message.patient_uuid && message.therapist_uuid)
    );
  };
  
  // Format message timestamp
  const getMessageTime = (message) => {
    // Use created_at or timestamp depending on source
    const timeValue = message.created_at || message.timestamp;
    return formatTime(timeValue);
  };
  
  // Group messages by sender for better UI
  const renderMessages = () => {
    // Use chat messages or fallback to preloaded messages
    const messagesToRender = (chat.messages && chat.messages.length > 0) 
      ? chat.messages 
      : (preloadedMessages || []);
      
    if (!messagesToRender || messagesToRender.length === 0) {
      return (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          height: '100%',
          color: 'text.secondary'
        }}>
          <Typography variant="body2">No messages yet. Start the conversation!</Typography>
        </Box>
      );
    }
    
    // Transform messages to have consistent structure
    const normalizedMessages = messagesToRender.map(message => ({
      ...message,
      id: message.id || message.message_uuid,
      fromMe: isMessageFromMe(message),
      content: message.content,
      timestamp: message.created_at || message.timestamp
    }));
    
    // Group consecutive messages from the same sender
    let groups = [];
    let currentGroup = {
      fromMe: normalizedMessages[0].fromMe,
      messages: [normalizedMessages[0]]
    };
    
    for (let i = 1; i < normalizedMessages.length; i++) {
      const message = normalizedMessages[i];
      
      if (message.fromMe === currentGroup.fromMe) {
        // Add to current group
        currentGroup.messages.push(message);
      } else {
        // Finish current group and start a new one
        groups.push(currentGroup);
        currentGroup = {
          fromMe: message.fromMe,
          messages: [message]
        };
      }
    }
    
    // Add the last group
    groups.push(currentGroup);
    
    return groups.map((group, groupIndex) => (
      <Box
        key={`group-${groupIndex}`}
        sx={{
          alignSelf: group.fromMe ? 'flex-end' : 'flex-start',
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          maxWidth: '80%',
          mb: 1
        }}
      >
        {group.messages.map((message, messageIndex) => (
          <Box
            key={message.id}
            sx={{
              bgcolor: group.fromMe ? 'primary.main' : 'white',
              color: group.fromMe ? 'white' : 'inherit',
              p: 1.5,
              px: 2,
              borderRadius: 2,
              boxShadow: 1,
              ...(messageIndex === 0 && {
                borderTopLeftRadius: group.fromMe ? 16 : 4,
                borderTopRightRadius: group.fromMe ? 4 : 16, 
              }),
              ...(messageIndex === group.messages.length - 1 && {
                borderBottomLeftRadius: group.fromMe ? 16 : 4,
                borderBottomRightRadius: group.fromMe ? 4 : 16,
              }),
              ...(messageIndex !== group.messages.length - 1 && {
                borderBottomLeftRadius: 4,
                borderBottomRightRadius: 4,
              })
            }}
          >
            <Typography variant="body2" sx={{ 
              textAlign: 'left'
            }}>{message.content}</Typography>
            {messageIndex === group.messages.length - 1 && (
              <Typography 
                variant="caption" 
                sx={{ 
                  display: 'block', 
                  textAlign: 'right',
                  mt: 0.5,
                  opacity: 0.8
                }}
              >
                {getMessageTime(message)}
              </Typography>
            )}
          </Box>
        ))}
      </Box>
    ));
  };
  
  return (
    <Paper
      elevation={3}
      sx={{
        position: 'fixed',
        bottom: 20,
        right: rightPosition,
        width: 300,
        height: 400,
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        zIndex: 1000,
      }}
    >
      {/* Chat header */}
      <Box
        sx={{
          p: 1,
          bgcolor: 'primary.main',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar 
            sx={{ 
              bgcolor: stringToColor(contact.name),
              width: 32, 
              height: 32, 
              mr: 1,
              position: 'relative'
            }}
          >
            {getInitials(contact.name)}
            {contact.isOnline && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: -2,
                  right: -2,
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: 'success.main',
                  border: '1px solid white'
                }}
              />
            )}
          </Avatar>
          <Box>
            <Typography variant="subtitle2" sx={{ lineHeight: 1.2 }}>{contact.name}</Typography>
            <Typography variant="caption" sx={{ fontSize: '0.65rem', opacity: 0.9 }}>
              {contact.isOnline ? 'Online' : 'Offline'}
            </Typography>
          </Box>
        </Box>
        
        <IconButton 
          size="small" 
          sx={{ color: 'white' }} 
          onClick={() => closeChat(chat.id || chat.chat_uuid)}
        >
          <CloseRounded />
        </IconButton>
      </Box>
      
      {/* Chat messages */}
      <Box
        sx={{
          p: 2,
          flexGrow: 1,
          overflow: 'auto',
          bgcolor: '#f5f5f5',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {renderMessages()}
        <div ref={messagesEndRef} />
      </Box>
      
      {/* Message input */}
      <Box component="form" onSubmit={handleSubmit} sx={{ p: 1, bgcolor: 'white' }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Type a message..."
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          variant="outlined"
          multiline
          maxRows={3}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton 
                  type="submit" 
                  disabled={!newMessage.trim()}
                  color="primary"
                >
                  <svg 
                    width="20" 
                    height="20" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path 
                      d="M22 2L11 13" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                    <path 
                      d="M22 2L15 22L11 13L2 9L22 2Z" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>
    </Paper>
  );
};

export default ChatWindow;